import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { CheckCircleLightGlyph, ExclamationTriangleLightGlyph, InfoCircleLightGlyph } from '@acast-tech/decibel-glyphs';
import T from 'prop-types';
import styled from 'styled-components';
import { black, black72, blackAlpha32, purple, purpleDarken16, green, greenDarken16, red, redDarken16, white, yellow, yellowDarken16 } from '../../../styles/colors';
import { body2 } from '../../../styles/typography';
import { unit } from '../../../styles/unit';
import { Icon } from '../../Icon/Icon';
import { ThemeProvider } from '../../ThemeProvider/ThemeProvider';
import { toastPositions } from '../lib/positions';
import { animationStyle } from '../lib/toastAnimationStyle';
import { toastTypes } from '../lib/toastTypes';
import { toast } from '../toast';
import { Action } from './Action';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const intentStyleMap = {
  primary: {
    glyph: InfoCircleLightGlyph,
    glyphColor: white,
    backgroundColor: purple,
    borderColor: purpleDarken16,
    textColor: white
  },
  secondary: {
    glyph: InfoCircleLightGlyph,
    glyphColor: white,
    backgroundColor: black72,
    borderColor: black,
    textColor: white
  },
  success: {
    glyph: CheckCircleLightGlyph,
    glyphColor: white,
    backgroundColor: green,
    borderColor: greenDarken16,
    textColor: white
  },
  warning: {
    glyph: InfoCircleLightGlyph,
    glyphColor: black,
    backgroundColor: yellow,
    borderColor: yellowDarken16,
    textColor: black
  },
  danger: {
    glyph: ExclamationTriangleLightGlyph,
    glyphColor: white,
    backgroundColor: red,
    borderColor: redDarken16,
    textColor: white
  }
};
const ToastContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "ToastContainer",
  componentId: "sc-1nnf220-0"
})(["min-width:400px;display:flex;flex-direction:row;padding:", ";background-color:", ";border:1px solid ", ";border-radius:8px;align-items:center;box-shadow:0 4px 6px ", ";color:", ";animation:", ";", ";"], unit(4), props => props.backgroundColor, props => props.borderColor, blackAlpha32, props => props.textColor, props => animationStyle(props.position), props => props.customStyle);
const IconContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "IconContainer",
  componentId: "sc-1nnf220-1"
})(["width:24px;height:24px;flex:0 0 auto;display:flex;align-items:center;justify-content:center;margin-right:", ";"], unit(1));
const ContentContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "ContentContainer",
  componentId: "sc-1nnf220-2"
})(["display:flex;justify-content:space-between;flex-grow:1;flex-direction:row;align-items:center;"]);
const Body = /*#__PURE__*/styled.div.withConfig({
  displayName: "Body",
  componentId: "sc-1nnf220-3"
})(["", ";white-space:pre-line;color:inherit;margin:0 ", " 0;"], body2, unit(2));
const ActionContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "ActionContainer",
  componentId: "sc-1nnf220-4"
})(["display:flex;>:not(:last-child){margin-right:", ";}"], unit(2));
const darkTheme = {
  type: 'dark'
};

const Toast = ({
  id,
  closable = false,
  intent = 'primary',
  customGlyph,
  customStyle,
  children,
  actions = [],
  position = 'bottom'
}) => {
  const _ref = intentStyleMap[intent] || {},
        {
    glyph,
    glyphColor
  } = _ref,
        intentStyles = _objectWithoutPropertiesLoose(_ref, ["glyph", "glyphColor"]);

  return /*#__PURE__*/_jsxs(ToastContainer, Object.assign({
    role: "alert"
  }, intentStyles, {
    customStyle: customStyle,
    position: position,
    children: [/*#__PURE__*/_jsx(IconContainer, {
      children: /*#__PURE__*/_jsx(Icon, {
        glyph: customGlyph || glyph,
        primaryColor: glyphColor
      })
    }), /*#__PURE__*/_jsxs(ContentContainer, {
      children: [/*#__PURE__*/_jsx(Body, {
        children: children
      }), /*#__PURE__*/_jsx(ThemeProvider, {
        theme: intentStyles.textColor === white ? darkTheme : {},
        children: /*#__PURE__*/_jsxs(ActionContainer, {
          children: [actions.map((action, index) => /*#__PURE__*/_jsx(Action // eslint-disable-next-line react/no-array-index-key
          , {
            onClick: () => {
              action.onClick();
              toast.close(id);
            },
            children: action.text
          }, index)), closable && /*#__PURE__*/_jsx(Action, {
            onClick: () => toast.close(id),
            children: "Close"
          })]
        })
      })]
    })]
  }));
};

Toast.propTypes = {
  id: T.number.isRequired,
  intent: T.oneOf(Object.values(toastTypes)),
  customGlyph: T.elementType,
  closable: T.bool,
  customStyle: T.arrayOf(T.string),
  position: T.oneOf(Object.values(toastPositions)),
  children: T.node,
  actions: T.arrayOf(T.shape({
    text: T.string,
    onClick: T.func
  }))
};
export { Toast };